import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import useBranch from '../../hooks/useBranch';
import useEntertainment from '../../hooks/useEntertainment';
import useBooking from '../../hooks/useBooking';
import useLasertagGame from '../../hooks/useLasertagGame';
import { useAuth } from '../../context/authContext';
import EntertainmentCapacity from '../Bookings/components/EntertainmentCapacity';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DangerousIcon from '@mui/icons-material/Dangerous';
import { getSlotColorById, getSlotLabelById } from '../../util/operatorUtils';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const LasertagStatistics = () => {
  const theme = useTheme();
  const { checkRight } = useAuth();
  const { getBranches } = useBranch();
  const { getAllEntertainmentsByBranch } = useEntertainment();
  const { getResourcesBySingleEntertainmentDay } = useBooking();
  const { getLasertagGameStatistics } = useLasertagGame();

  const [branchOptions, setBranchOptions] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [entertainmentOptions, setEntertainmentOptions] = useState([]);
  const [entertainmentId, setEntertainmentId] = useState('');
  const [date, setDate] = useState(() => dayjs());
  const [capacity, setCapacity] = useState(null);
  const [games, setGames] = useState([]);
  const [averages, setAverages] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Guard by statistic right on frontend side as well
  const hasStatisticRight = checkRight('statistic:read:own');

  useEffect(() => {
    let cancelled = false;
    const loadBranches = async () => {
      try {
        const branches = await getBranches();
        if (cancelled) return;
        const options = (branches || []).map(b => ({ id: b._id, label: b.locationName }));
        setBranchOptions(options);

        let initialBranch = '';
        try {
          const storedBranch = sessionStorage.getItem('branch');
          if (storedBranch && options.some(o => o.id === storedBranch)) {
            initialBranch = storedBranch;
          } else if (options.length > 0) {
            initialBranch = options[0].id;
          }
        } catch (_) {
          if (options.length > 0) {
            initialBranch = options[0].id;
          }
        }
        setBranchId(initialBranch || '');
      } catch (err) {
        try {
          toast.error('Fehler beim Laden der Standorte');
        } catch (_) {}
        if (!cancelled) {
          setBranchOptions([]);
          setBranchId('');
        }
      }
    };
    if (hasStatisticRight) {
      loadBranches();
    }
    return () => {
      cancelled = true;
    };
  }, [hasStatisticRight]);

  useEffect(() => {
    let cancelled = false;
    const loadEntertainments = async () => {
      if (!branchId) {
        setEntertainmentOptions([]);
        setEntertainmentId('');
        return;
      }
      try {
        const list = await getAllEntertainmentsByBranch(branchId);
        if (cancelled) return;
        const filtered = (list || []).filter(ent => {
          if (!ent || typeof ent.entertainmentName !== 'string') {
            return false;
          }
          return ent.entertainmentName.trim().toLowerCase() === 'laserzone';
        });
        const options = filtered.map(e => ({ id: e._id, label: e.entertainmentName }));
        setEntertainmentOptions(options);
        if (options.length > 0) {
          setEntertainmentId(options[0].id);
        } else {
          setEntertainmentId('');
        }
      } catch (err) {
        try {
          toast.error('Fehler beim Laden der Aktivitäten');
        } catch (_) {}
        if (!cancelled) {
          setEntertainmentOptions([]);
          setEntertainmentId('');
        }
      }
    };
    if (hasStatisticRight) {
      loadEntertainments();
    }
    return () => {
      cancelled = true;
    };
  }, [branchId, hasStatisticRight]);

  useEffect(() => {
    const loadData = async () => {
      if (!hasStatisticRight || !entertainmentId || !date || !date.isValid()) {
        setGames([]);
        setAverages(null);
        setCapacity(null);
        return;
      }

      setIsLoading(true);
      try {
        const dayString = date.format('YYYY-MM-DD');

        // Load capacity data for header
        try {
          const capacityData = await getResourcesBySingleEntertainmentDay({
            date: dayString,
            entertainmentId,
          });
          if (capacityData && capacityData.success) {
            setCapacity(capacityData.data);
          } else {
            setCapacity(null);
          }
        } catch (err) {
          setCapacity(null);
        }

        // Load statistics from backend
        const stats = await getLasertagGameStatistics({
          entertainmentId,
          date: dayString,
        });

        const data = stats || {};
        const gamesFromApi = Array.isArray(data.games) ? data.games : data?.data?.games || [];
        const averagesFromApi = data.averages || data?.data?.averages || null;

        // Ensure local sort by startTime ascending
        const normalizedGames = (gamesFromApi || []).slice().sort((a, b) => {
          const aTime = a.startTime ? new Date(a.startTime).getTime() : 0;
          const bTime = b.startTime ? new Date(b.startTime).getTime() : 0;
          return aTime - bTime;
        });

        setGames(normalizedGames);
        setAverages(averagesFromApi);
      } catch (err) {
        try {
          toast.error('Fehler beim Laden der Lasertag-Statistik');
        } catch (_) {}
        setGames([]);
        setAverages(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [hasStatisticRight, entertainmentId, date]);

  const { entries: gamesWithGaps, medalSummary } = useMemo(() => {
    const list = games || [];
    const result = [];
    const medals = {
      gold: 0,
      silver: 0,
      bronze: 0,
      warning: 0,
      danger: 0,
    };

    const slotGold = theme.palette.success.main;
    const slotSilver = theme.palette.grey[400];
    const slotBronze = theme.palette.warning.main;
    const slotDanger = theme.palette.secondary.main;
    const slotWarn = theme.palette.error.main;

    for (let i = 0; i < list.length; i++) {
      const current = list[i];
      const next = i + 1 < list.length ? list[i + 1] : null;
      result.push({
        type: 'game',
        game: current,
      });
      if (next && current.endTime && next.startTime) {
        const endTs = new Date(current.endTime).getTime();
        const startTs = new Date(next.startTime).getTime();
        let gapSeconds = (startTs - endTs) / 1000;
        if (!Number.isFinite(gapSeconds) || gapSeconds < 0) {
          gapSeconds = 0;
        }

        const currentIsSlot = current.slotId === 0 || current.slotId === 1;
        const nextIsSlot = next.slotId === 0 || next.slotId === 1;
        const isSlotPair = currentIsSlot && nextIsSlot;
        const isSlotChange =
          isSlotPair &&
          current.slotId !== null &&
          next.slotId !== null &&
          current.slotId !== next.slotId;

        let color = null;
        let category = null;

        if (isSlotPair) {
          // Slot pair classification (Gold/Silber/Bronze/Warn/Danger)
          if (gapSeconds < 4 * 60) {
            category = 'gold';
            color = slotGold;
            if (isSlotChange) medals.gold += 1;
          } else if (gapSeconds >= 4 * 60 && gapSeconds < 5 * 60) {
            category = 'silver';
            color = slotSilver;
            if (isSlotChange) medals.silver += 1;
          } else if (gapSeconds >= 5 * 60 && gapSeconds < 6 * 60) {
            category = 'bronze';
            color = slotBronze;
            if (isSlotChange) medals.bronze += 1;
          } else if (gapSeconds > 8 * 60) {
            category = 'danger';
            color = slotDanger;
            if (isSlotChange) medals.danger += 1;
          } else if (gapSeconds >= 6 * 60) {
            category = 'warning';
            color = slotWarn;
            if (isSlotChange) medals.warning += 1;
          }
        } else {
          // Mixed pair classification (no medals, only colors)
          if (gapSeconds < 3 * 60) {
            color = slotDanger;
            category = 'mixed-short';
          } else if (gapSeconds < 6 * 60) {
            color = slotGold;
            category = 'mixed-green';
          } else if (gapSeconds <= 10 * 60) {
            color = slotSilver;
            category = 'mixed-yellow';
          } else {
            color = slotWarn;
            category = 'mixed-red';
          }
        }

        result.push({
          type: 'gap',
          fromId: current.id,
          toId: next.id,
          gapSeconds,
          isSlotPair,
          color,
          category,
        });
      }
    }
    return { entries: result, medalSummary: medals };
  }, [games, theme]);

  const formatGap = seconds => {
    if (seconds == null || !Number.isFinite(seconds)) return '–';
    const total = Math.max(0, Math.round(seconds));
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const renderGameCard = game => {
    const slotId = typeof game.slotId === 'number' ? game.slotId : null;
    const hasSlotColor = slotId === 0 || slotId === 1;
    const backgroundColor = hasSlotColor
      ? getSlotColorById(slotId, theme)
      : theme.palette.background.paper;
    const textColor = hasSlotColor
      ? theme.palette.getContrastText(backgroundColor)
      : theme.palette.text.primary;
    const slotLabel = slotId === null ? 'Kein Slot' : getSlotLabelById(slotId);

    const start = game.startTime ? dayjs(game.startTime) : null;
    const end = game.endTime ? dayjs(game.endTime) : null;

    return (
      <Paper
        key={game.id}
        elevation={3}
        sx={{
          mb: 1.5,
          p: 1.5,
          backgroundColor,
          color: textColor,
        }}
      >
        <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
          <Stack spacing={0.5}>
            <Typography variant='subtitle2' sx={{ opacity: 0.9 }}>
              {slotLabel}
            </Typography>
            <Typography variant='body2'>
              Start: {start && start.isValid() ? start.format('HH:mm:ss') : '–'}
            </Typography>
            <Typography variant='body2'>
              Ende: {end && end.isValid() ? end.format('HH:mm:ss') : '–'}
            </Typography>
          </Stack>
          <Stack alignItems='flex-end'>
            <Typography variant='h5' sx={{ fontWeight: 600 }}>
              {typeof game.playerCount === 'number' ? game.playerCount : 0}
            </Typography>
            <Typography variant='body2' sx={{ opacity: 0.9 }}>
              Spieler
            </Typography>
          </Stack>
        </Stack>
      </Paper>
    );
  };

  if (!hasStatisticRight) {
    return (
      <Box sx={{ ...viewRootTextPrimarySx, p: 2 }}>
        <Typography variant='h6'>Kein Zugriff auf die Lasertag-Statistik.</Typography>
      </Box>
    );
  }

  const slotOnly = averages?.slotOnly || averages?.slot_only || null;
  const mixedSlots = averages?.mixedSlots || averages?.mixed_slots || null;

  const hasAnyMedals =
    (medalSummary?.gold || 0) +
      (medalSummary?.silver || 0) +
      (medalSummary?.bronze || 0) +
      (medalSummary?.warning || 0) +
      (medalSummary?.danger || 0) >
    0;

  const handleBranchChange = event => {
    const value = event.target.value;
    setBranchId(value);
    try {
      sessionStorage.setItem('branch', value);
    } catch (_) {}
  };

  return (
    <Box
      sx={{
        ...viewRootTextPrimarySx,
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      <Paper
        sx={{
          mb: 2,
          p: 2,
          flexShrink: 0,
        }}
        elevation={3}
      >
        <Stack
          direction='row'
          spacing={1.5}
          alignItems='center'
          flexWrap='wrap'
          useFlexGap
          sx={{
            '& > *': {
              flexShrink: 0,
            },
          }}
        >
          <DatePicker
            label='Datum'
            value={date}
            slotProps={{
              textField: {
                size: 'small',
                sx: {
                  width: { xs: '100%', sm: 160 },
                  minWidth: 150,
                  flex: { xs: 1, sm: 'none' },
                },
              },
            }}
            onChange={newValue => {
              setDate(newValue || dayjs());
            }}
          />

          {branchOptions.length > 1 && (
            <FormControl
              size='small'
              sx={{
                width: { xs: '100%', sm: 160 },
                minWidth: 140,
                flex: { xs: 1, sm: 'none' },
              }}
            >
              <InputLabel>Branch</InputLabel>
              <Select value={branchId} label='Branch' onChange={handleBranchChange}>
                {branchOptions.map(br => (
                  <MenuItem key={br.id} value={br.id}>
                    {br.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </Paper>

      {capacity && (
        <Box sx={{ mb: 2 }}>
          <EntertainmentCapacity data={capacity} condensedMode />
        </Box>
      )}

      <Paper
        sx={{
          mb: 2,
          p: 2,
          flexShrink: 0,
        }}
        elevation={3}
      >
        <Typography variant='h6' gutterBottom>
          Durchschnittliche Lücken am Tag
        </Typography>
        <Stack spacing={1}>
          <Typography variant='body2'>
            Beide Spiele in Slots (0/1):{' '}
            <strong>
              {slotOnly && slotOnly.averageGapSeconds != null
                ? formatGap(slotOnly.averageGapSeconds)
                : '–'}
            </strong>
            {slotOnly && typeof slotOnly.count === 'number' && <> ({slotOnly.count} Lücken)</>}
          </Typography>
          <Typography variant='body2'>
            Mindestens ein Spiel ohne Slot (leer/2):{' '}
            <strong>
              {mixedSlots && mixedSlots.averageGapSeconds != null
                ? formatGap(mixedSlots.averageGapSeconds)
                : '–'}
            </strong>
            {mixedSlots && typeof mixedSlots.count === 'number' && (
              <> ({mixedSlots.count} Lücken)</>
            )}
          </Typography>
        </Stack>
        {hasAnyMedals && (
          <Stack
            direction='row'
            spacing={2}
            mt={2}
            flexWrap='wrap'
            useFlexGap
            sx={{
              '& > *': {
                flexShrink: 0,
              },
            }}
          >
            {medalSummary.gold > 0 && (
              <Stack direction='row' spacing={0.5} alignItems='center'>
                <EmojiEventsIcon sx={{ color: 'success.main' }} />
                <Typography variant='body2'>Gold: {medalSummary.gold}</Typography>
              </Stack>
            )}
            {medalSummary.silver > 0 && (
              <Stack direction='row' spacing={0.5} alignItems='center'>
                <EmojiEventsIcon sx={{ color: theme.palette.grey[400] }} />
                <Typography variant='body2'>Silber: {medalSummary.silver}</Typography>
              </Stack>
            )}
            {medalSummary.bronze > 0 && (
              <Stack direction='row' spacing={0.5} alignItems='center'>
                <EmojiEventsIcon sx={{ color: 'warning.main' }} />
                <Typography variant='body2'>Bronze: {medalSummary.bronze}</Typography>
              </Stack>
            )}
            {medalSummary.warning > 0 && (
              <Stack direction='row' spacing={0.5} alignItems='center'>
                <WarningAmberIcon sx={{ color: 'error.main' }} />
                <Typography variant='body2'>Warnungen: {medalSummary.warning}</Typography>
              </Stack>
            )}
            {medalSummary.danger > 0 && (
              <Stack direction='row' spacing={0.5} alignItems='center'>
                <DangerousIcon sx={{ color: 'secondary.main' }} />
                <Typography variant='body2'>Gefahr: {medalSummary.danger}</Typography>
              </Stack>
            )}
          </Stack>
        )}
      </Paper>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          pb: 2,
        }}
      >
        {isLoading && (
          <Typography sx={{ mb: 2 }} variant='body2'>
            Lade Daten...
          </Typography>
        )}
        {!isLoading && gamesWithGaps.length === 0 && (
          <Typography variant='body2'>Keine Spiele für den ausgewählten Tag gefunden.</Typography>
        )}
        {!isLoading &&
          gamesWithGaps.map((entry, idx) => {
            if (entry.type === 'game') {
              return renderGameCard(entry.game);
            }
            return (
              <Box
                key={`gap-${entry.fromId}-${entry.toId}-${idx}`}
                sx={{
                  my: 0.5,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {entry.category === 'danger' ? (
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <DangerousIcon sx={{ color: entry.color || 'secondary.main' }} />
                    <Typography
                      variant='h6'
                      sx={{
                        color: entry.color || 'text.secondary',
                        fontWeight: 700,
                      }}
                    >
                      {formatGap(entry.gapSeconds)}
                    </Typography>
                  </Stack>
                ) : entry.category === 'warning' ? (
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <WarningAmberIcon sx={{ color: entry.color || 'error.main' }} />
                    <Typography
                      variant='h6'
                      sx={{
                        color: entry.color || 'text.secondary',
                        fontWeight: 700,
                      }}
                    >
                      {formatGap(entry.gapSeconds)}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography
                    variant='h6'
                    sx={{
                      color: entry.color || 'text.secondary',
                      fontWeight: 700,
                    }}
                  >
                    {formatGap(entry.gapSeconds)}
                  </Typography>
                )}
              </Box>
            );
          })}
      </Box>
    </Box>
  );
};

export default LasertagStatistics;
